import type { TargetingTableRowGroup } from '../../../../../../../../../common/TargetingTable/TargetingTable';
import type { AdType } from '../../../../../../../../../configs';
import { UNRECOGNIZED } from '../../../../../../../../../configs';
import type { LineItem, TargetingTermValue } from '../../../../../../../../../models';
import { EntityTypeArray } from '../../../../../../../TraffickingPage/hooks/makePageDrawerPlugin';
import type { AdEntity } from '../../../../../types';

/**
 * Build a `TargetingTableRowGroup[]` for the multi-edit Targeting card. The
 * card shows a single Formik field (`targetingTermValues`) of mixed shape, but
 * the `TargetingTable` always renders both an "Ad Targeting" and a "Line Item
 * Targeting" group; the `isLineItem` flag selects which group hosts the data.
 *
 * Distinct from `generateTargetingTableData` in
 * `pages/Trafficking/DrawerTargetingTable/utils/utils.ts`, which takes
 * pre-split ad/line-item arrays.
 */
export const generateMultiEditTargetingTableData = (
  targetingTermValues: TargetingTermValue[],
  isLineItem: boolean
): TargetingTableRowGroup[] => [
  {
    groupType: 'Ad Targeting',
    targetingTermValues: isLineItem ? [] : targetingTermValues,
  },
  {
    groupType: 'Line Item Targeting',
    targetingTermValues: isLineItem ? targetingTermValues : [],
  },
];

/**
 * Header copy shown above the multi-edit Targeting card on Step 2 of the
 * MultipleEditDrawer.
 *
 * Distinct from `getEntitiesConfirmationHeaderText` in
 * `MultipleEditDrawerContentFirstStep/utils.ts`, which renders Step 1 copy.
 */
export const getTargetingHeaderText = (entityType: EntityTypeArray): string => {
  const isLineItem = entityType === EntityTypeArray.LINE_ITEM_ARRAY;

  return `Choose a targeting value to apply to the selected ${
    isLineItem ? 'line items' : 'ads'
  }. This setting will be in addition to other targeting previously set for the ${isLineItem ? 'line items' : 'ads'}.`;
};

export const getUniqueAdTypeKeys = (adEntities: AdEntity[]): AdType[] => {
  const uniqueKeys = adEntities.reduce(
    (acc, entity) => {
      const adTypeKey = entity.type?.key;

      if (adTypeKey && adTypeKey !== UNRECOGNIZED) {
        acc.adTypes.add(adTypeKey);
      }

      return acc;
    },
    {
      adTypes: new Set<AdType>(),
    }
  );

  return Array.from(uniqueKeys.adTypes);
};

// ---------------------------------------------------------------------------
// Multi-edit `TargetingScope` unions
//
// These helpers compute the de-duplicated union of country / publisher fields
// across the entities currently being multi-edited. The result is fed into
// `TargetingScope` (see `app/src/common/Targeting/types.ts`) and used by
// `TargetingDropdowns` / `TargetingValuesDropdown` / `useTargetingValuesDropdown`
// to filter dimensions, filter values, and bound the targeting-values fetch.
//
// Field-source contract (per design):
//   - Ad:        countries  ← `entity.countriesTarget`
//                publishers ← `entity.displayPublisherTarget`
//                              fall back to `entity.lineItem.displayPublisherTarget`
//                              when the entity-level value is null
//   - LineItem:  countries  ← `entity.countries`
//                publishers ← `entity.displayPublisherTarget`
// ---------------------------------------------------------------------------

const dedupe = (values: ReadonlyArray<string | undefined | null>): string[] => {
  const set = new Set<string>();
  for (const v of values) {
    if (v) set.add(v);
  }
  return Array.from(set);
};

/** Union of `entity.countries` across the supplied line items. */
export const unionLineItemCountries = (lineItems: LineItem[]): string[] =>
  dedupe(lineItems.flatMap((li) => li.countries ?? []));

/** Union of `entity.lineItem.countries` across the supplied ads (parent-line scope). */
export const unionAdLineItemCountries = (adEntities: AdEntity[]): string[] =>
  dedupe(adEntities.flatMap((ad) => ad.lineItem?.countries ?? []));

/** Union of `entity.countriesTarget` across the supplied ads (the ad's own targeted countries). */
export const unionAdCountriesTarget = (adEntities: AdEntity[]): string[] =>
  dedupe(adEntities.flatMap((ad) => ad.countriesTarget ?? []));

/** Union of `entity.displayPublisherTarget` across the supplied line items. */
export const unionLineItemPublishers = (lineItems: LineItem[]): string[] =>
  dedupe(lineItems.flatMap((li) => li.displayPublisherTarget ?? []));

/**
 * Union of publisher IDs across the supplied ads. Per the multi-edit
 * field-source contract, prefers `entity.displayPublisherTarget`, falling back
 * to `entity.lineItem.displayPublisherTarget` only when the entity-level value
 * is `null` / `undefined`. (`[]` on the entity wins; the fallback only triggers
 * when the field is absent entirely.)
 */
export const unionAdPublishers = (adEntities: AdEntity[]): string[] =>
  dedupe(
    adEntities.flatMap((ad) => {
      const own = ad.displayPublisherTarget;
      if (own !== null && own !== undefined) return own;
      return ad.lineItem?.displayPublisherTarget ?? [];
    })
  );

/**
 * `true` when the supplied multi-edit entities do NOT all share the same set
 * of country targets (Ads → `countriesTarget`; Line Items → `countries`).
 *
 * Returns `false` for collections of 0 or 1 entities (nothing to mismatch).
 */
export const computeMismatchedCountries = (entities: LineItem[] | AdEntity[], isAd: boolean): boolean => {
  if (entities.length < 2) return false;

  const sets: Array<Set<string>> = (entities as Array<LineItem | AdEntity>).map((entity) => {
    const ids = isAd ? (entity as AdEntity).countriesTarget ?? [] : (entity as LineItem).countries ?? [];
    return new Set<string>(ids);
  });

  const first = sets[0];
  return !sets.every((s) => s.size === first.size && [...s].every((v) => first.has(v)));
};

/**
 * `true` when the supplied multi-edit entities do NOT all share the same set
 * of publishers. Per-entity publisher source mirrors the union helpers above:
 *   - Line Item: `entity.displayPublisherTarget` (treat null as []).
 *   - Ad:        prefer `entity.displayPublisherTarget`; fall back to
 *                `entity.lineItem.displayPublisherTarget` only when the entity-
 *                level value is `null` / `undefined` (`[]` is "explicitly no
 *                publishers" and wins).
 *
 * Returns `false` for collections of 0 or 1 entities (nothing to mismatch).
 */
export const computeMismatchedPublishers = (entities: LineItem[] | AdEntity[], isAd: boolean): boolean => {
  if (entities.length < 2) return false;

  const sets: Array<Set<string>> = (entities as Array<LineItem | AdEntity>).map((entity) => {
    if (isAd) {
      const ad = entity as AdEntity;
      const own = ad.displayPublisherTarget;
      const ids = own ?? ad.lineItem?.displayPublisherTarget ?? [];
      return new Set<string>(ids);
    }
    return new Set<string>((entity as LineItem).displayPublisherTarget ?? []);
  });

  const first = sets[0];
  return !sets.every((s) => s.size === first.size && [...s].every((v) => first.has(v)));
};
